'use client';

// biome-ignore lint/style/noRestrictedImports: Used to create our custom Link component
import { default as NextLink } from 'next/link';
import { forwardRef } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { getPageViewDataLayerObject, type TrackEventParams, trackEvent } from '@/app_lib/services/Gtm/utils';
import { cn } from '@/app_lib/utils/utils';

const contentLink = tv({
  variants: {
    variant: {
      primary: 'text-blue-500 dark:text-blue-300 text-primary-hover text-primary-pressed text-primary-visited',
      secondary: 'text-red-500 dark:text-red-400 hover:underline',
    },
  },
});

const navLink = tv({
  base: 'font-medium',
  variants: {
    variant: {
      primary: 'hover:text-secondary active:text-secondary',
      secondary: 'hover:underline active:underline active:text-secondary',
    },
  },
});

type ContentLinkVariants = VariantProps<typeof contentLink>;
type NavLinkVariants = VariantProps<typeof navLink>;

export interface LinkProps extends ContentLinkVariants, NavLinkVariants, React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  className?: string;
  prefetch?: boolean;
  children: React.ReactNode;
  trackingData?: TrackEventParams;
  recipe?: 'content' | 'nav';
}

/**
 * Use this for all internal navigation.
 *
 * @param recipe - the styling group to be used (defaults to no styling)
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      href,
      className,
      prefetch = true,
      children,
      variant,
      onClick,
      trackingData,
      recipe,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) => {
    const style = recipe === 'content' ? contentLink : navLink;

    return (
      <NextLink
        aria-label={ariaLabel || `Go to ${href}`}
        className={recipe ? cn(className, style({ variant })) : className}
        href={encodeURI(href)}
        onClick={(e) => {
          // TODO: check with data team to see if we can track failures
          if (trackingData) {
            const pageData = getPageViewDataLayerObject();
            trackEvent({ ...pageData, ...trackingData.data }, trackingData.eventType);
          }
          onClick?.(e);
        }}
        prefetch={prefetch}
        ref={ref}
        {...rest}
      >
        {children}
      </NextLink>
    );
  },
);
